<template>
  <section class="block-wrapper" :class="{'-header-scroll-up': hideHeader}">
    <div
      :id="`block-${block.__id__}`"
      v-for="(block, index) in blockList"
      v-bind:key="index"
      :class="[
        `-${getBlockType(block)}`,
        {
          '-bgcolor': hasBackgroundColor(block) || block.DarkMode,
          '-blank':
            !(hasBackgroundColor(block) || block.DarkMode || darkMode) &&
            !(
              getBlockType(block) === 'HeroBlock' ||
              getBlockType(block) === 'TestimonialSliderBlock' ||
              getBlockType(block) === 'FabricTechnologyBlock' ||
              getBlockType(block) === 'HighlightBanner' ||
              getBlockType(block) === 'TabWrapperBlock'
            ),
          '-fullimage':
            getBlockType(block) === 'HeroBlock' ||
            getBlockType(block) === 'TestimonialSliderBlock' ||
            getBlockType(block) === 'FabricTechnologyBlock' ||
            getBlockType(block) === 'CSRBlock' ||
            getBlockType(block) === 'TabWrapperBlock' ||
            getBlockType(block) === 'B2BDigitalWorkbookBlock' ||
            getBlockType(block) === 'ProductsInActionBlock',

            '-header-no-scroll': staticHeader
        },
      ]"
      :style="blockStyle(block)"
    >
      <div
        v-if="isVisible(block)"
        class=""
        :class="[
          container(block),
          {
            '-inline': inline,
            'content-area':
              getBlockType(block) !== 'ProductFeatureBlock' &&
              getBlockType(block) !== 'ProductTechBlock' &&
              getBlockType(block) !== 'TabWrapperBlock',
            grid: getBlockType(block) === 'FactoryBlock',
          },
        ]"
      >
        <component
          :rowid="`${index}`"
          :index="index"
          :typeIndex="similarBlockCount(block)"
          :settings="settings(block)"
          :model="block"
          :is="getBlockType(block)"
          :inline="inline"
          :ready="true"
          :pageType="pageType"
          :blockIsScrolledTo="scrollToBlockIndex === index"
          :h1Title="!isStartPage && index === 0"
          :category="category"
          :listName="`${pageType}: ${category}`"
          :customData="customData"
          @ready="firstComponentReady(index === 0 || index === 1)"
          @on-change="$emit('on-change', $event)"
          :graymode="darkMode"
        />
      </div>
    </div>
  </section>
</template>

<script>
import { mapState } from 'vuex';
import TinyColor from 'tinycolor2';
import ScrollTo from 'vue-scrollto';
import HeroBlock from '@/Scripts/components/blocks/HeroBlock.vue';
import ProductRowBlock from '@/Scripts/components/blocks/ProductRowBlock.vue';
import VideoHeroBlock from '@/Scripts/components/blocks/VideoHeroBlock.vue';
import CampaignIconicBlock from '@/Scripts/components/blocks/CampaignIconicBlock.vue';

const ContentTextBlock = () => import('@/Scripts/components/blocks/ContentTextBlock.vue');
const MediaBlock = () => import('@/Scripts/components/blocks/MediaBlock.vue');
const MediaGalleryBlock = () => import('@/Scripts/components/blocks/MediaGalleryBlock.vue');
const Accordion = () => import('@/Scripts/components/blocks/AccordionBlock.vue');
const GoreWidgetBlock = () => import('@/Scripts/components/blocks/Gore.vue');
const FlowBox = () => import('@/Scripts/components/blocks/FlowBox.vue');
// const ProductRowBlock = () => import('@/Scripts/components/blocks/ProductRowBlock.vue');
const OneColumnBanner = () => import('@/Scripts/components/blocks/OneColumnBanner.vue');
const TwoColumnBanner = () => import('@/Scripts/components/blocks/TwoColumnBanner.vue');
const LoyaltyBanner = () => import('@/Scripts/components/blocks/LoyaltyBanner.vue');
const HighlightBanner = () => import('@/Scripts/components/blocks/HighlightBanner.vue');
const Teaser = () => import('@/Scripts/components/blocks/TeaserBlock.vue');
const FormContainerBlock = () => import('@/Scripts/components/shared/Form/FormContainerBlock.vue');
const Advanced = () => import('@/Scripts/components/blocks/Advanced.vue');
const ProductsInActionBlock = () => import('@/Scripts/components/blocks/ProductsInActionBlock.vue');
const ContentSliderBlock = () => import('@/Scripts/components/blocks/ContentSliderBlock.vue');
const VideoGalleryRowBlock = () => import('@/Scripts/components/blocks/VideoGalleryRowBlock.vue');
const SignupBlock = () => import('@/Scripts/components/blocks/SignupBlock.vue');
const TravelBlock = () => import('@/Scripts/components/blocks/TravelBlock.vue');
const EventsBlock = () => import('@/Scripts/components/blocks/EventsBlock.vue');
const IFrameBlock = () => import('@/Scripts/components/blocks/IFrameBlock.vue');
const ProductFeatureBlock = () => import(/* webpackChunkName: "ProductFeatureBlock" */ '@/Scripts/components/blocks/ProductFeatureBlock.vue');
const ProductTechBlock = () => import(/* webpackChunkName: "ProductTechBlock" */ '@/Scripts/components/blocks/ProductTechBlock.vue');
const FabricTechnologyBlock = () => import(/* webpackChunkName: "FabricTechnologyBlock" */ '@/Scripts/components/blocks/FabricTechnologyBlock.vue');
const CSRBlock = () => import(/* webpackChunkName: "CSRBlock" */ '@/Scripts/components/blocks/CSRBlock.vue');
const KeyFeaturesBlock = () => import('@/Scripts/components/blocks/KeyFeaturesBlock.vue');
const TestimonialSliderBlock = () => import('@/Scripts/components/blocks/TestimonialSliderBlock.vue');
const UseAndPerformanceBlock = () => import('@/Scripts/components/blocks/UseAndPerformanceBlock.vue');
const ProductSizeGuideBlock = () => import('@/Scripts/components/blocks/ProductSizeGuideBlock.vue');
const TabWrapperBlock = () => import('@/Scripts/components/blocks/TabWrapperBlock.vue');
const CSRGoalChartBlock = () => import('@/Scripts/components/blocks/CSRGoalChartBlock.vue');
const ReachMeeBlock = () => import('@/Scripts/components/blocks/ReachMeeBlock.vue');
const FactoryBlock = () => import('@/Scripts/components/blocks/FactoryBlock.vue');
const HowToDressBlock = () => import('@/Scripts/components/blocks/HowToDressBlock/HowToDressBlock.vue');
const B2BDigitalWorkbookBlock = () => import('@/Scripts/components/blocks/B2BDigitalWorkbookBlock.vue');
const B2BMarketingMaterialBlock = () => import('@/Scripts/components/blocks/B2BMarketingMaterialBlock.vue');
const PageNavigationBlock = () => import('@/Scripts/components/blocks/PageNavigationBlock.vue');
const ContactFormBlock = () => import('@/Scripts/components/blocks/ContactFormBlock.vue');
const StravaRouteEmbedBlock = () => import('@/Scripts/components/blocks/StravaRouteEmbedBlock.vue');
const PersonBlock = () => import('@/Scripts/components/blocks/PersonBlock.vue');

let printedBlocks = [];
let hashCheckCount = 0;

export default {
  components: {
    HeroBlock,
    VideoHeroBlock,
    UseAndPerformanceBlock,
    TestimonialSliderBlock,
    ContentTextBlock,
    MediaBlock,
    MediaGalleryBlock,
    Accordion,
    GoreWidgetBlock,
    FlowBox,
    ProductRowBlock,
    OneColumnBanner,
    TwoColumnBanner,
    LoyaltyBanner,
    HighlightBanner,
    Teaser,
    FormContainerBlock,
    Advanced,
    ProductsInActionBlock,
    ContentSliderBlock,
    VideoGalleryRowBlock,
    SignupBlock,
    TravelBlock,
    ProductFeatureBlock,
    ProductTechBlock,
    FabricTechnologyBlock,
    IFrameBlock,
    KeyFeaturesBlock,
    ProductSizeGuideBlock,
    TabWrapperBlock,
    CampaignIconicBlock,
    CSRBlock,
    CSRGoalChartBlock,
    ReachMeeBlock,
    FactoryBlock,
    ContactFormBlock,
    HowToDressBlock,
    B2BDigitalWorkbookBlock,
    B2BMarketingMaterialBlock,
    EventsBlock,
    PageNavigationBlock,
    StravaRouteEmbedBlock,
    PersonBlock

  },
  props: {
    model: {
      type: Array,
      required: true
    },
    inline: Boolean,
    fluid: Boolean,
    pageType: String,
    category: String,
    darkMode: Boolean,
    customData: Object,
    isStartPage: Boolean

  },
  data () {
    return {
      userVisitorGroups: null,
      scrollToBlockIndex: -1,
      fluidBlocks: [
        'GoreWidgetBlock',
        'ProductRowBlock',
        'HeroBlock',
        'CampaignIconicBlock',
        'OneColumnBanner',
        'TwoColumnBanner',
        'HighlightBanner',
        'LoyaltyBanner',
        'ContentSliderBlock',
        'ProductsInActionBlock',
        'ProductFeatureBlock',
        'ProductTechBlock',
        'FabricBlock',
        'TestimonialSliderBlock',
        'KeyFeaturesBlock'
      ],
      mobileFluid: ['AuctionPage']
    };
  },
  computed: {
    ...mapState({
      hideHeader: state => state.appContext.hideHeader,
      staticHeader: state => state.appContext.staticHeader
    }),
    blockList () {
      if (this.userVisitorGroups && this.userVisitorGroups.length > 0) {
        return this.model.map((block) => {
          if (!block.__content_group__) {
            return block; // block has no content group
          } else if (block.__allowed_groups__) {
            // check if block has usergroup
            let exist = false;
            block.__allowed_groups__.forEach(g => {
              if (!exist) {
                exist = this.userVisitorGroups.includes(g);
              }
            });
            if (exist) {
              return block; // block is in users content group
            }
          }
          // return default block if exist
          return this.model.find(i => i !== block && i.__content_group__ === block.__content_group__ && i.__default_content_group__) || false;
        }).filter(b => b !== false);
      }
      console.log('Model: ', this.model);
      return this.model;
    }
  },
  methods: {
    settings (block) {
      return {
        CSRGoalBlock: { csrType: 'goal' },
        CSRProblemBlock: { csrType: 'problem' }
      }[block.__type__];
    },
    firstComponentReady (val) {
      if (val) {
        this.scrollto();
        this.$emit('ready');
      }
    },
    getBlockType (block) {
      // block re-route [OldBlock: 'NewBlock']
      const replacingBlock = {
        OneColumnBanner: 'HeroBlock',
        MediaSliderBlock: 'ContentSliderBlock',
        ImageCollectorSliderBlock: 'ContentSliderBlock',
        CSRGoalBlock: 'CSRBlock',
        CSRProblemBlock: 'CSRBlock'
      }[block.__type__] || block.__type__;

      // block settings re-route
      if (replacingBlock === 'HeroBlock') {
        // Product and Collection video top toggler
        if (block.HeroStyle === 4) {
          return 'VideoHeroBlock';
        }
      } else if (replacingBlock === 'ContentSliderBlock') {
        if (block.SliderType === 2) {
          // video gallery row
          return 'VideoGalleryRowBlock';
        }
      }
      return replacingBlock;
    },
    similarBlockCount (block) {
      printedBlocks.push(block);
      return printedBlocks.filter(i => i.__type__ === block.__type__).length;
    },
    isVisible (block) {
      if (block.HideOnMobile && !this.$mqAliases.desktop) {
        return false;
      }
      if (block.HideOnDesktop && this.$mqAliases.desktop) {
        return false;
      }
      return true;
    },
    container (block) {
      const fluid = 'container-fluid-new px-0';

      if (this.fluid) {
        return fluid;
      }

      // Check for allways fluid block types
      for (let i = 0; i < this.fluidBlocks.length; i++) {
        if (block.__type__ === this.fluidBlocks[i]) {
          return fluid;
        }
      }
      // Check for mobile fluid block types
      if (this.$mqAliases.xs || this.$mqAliases.sm) {
        for (let i = 0; i < this.mobileFluid.length; i++) {
          if (block.__type__ === this.mobileFluid[i]) {
            return 'container-fluid-new px-0';
          }
        }
      }

      // Check for blocks with fluid settings
      if (
        typeof block.MediaPlacement !== 'undefined' &&
        block.MediaPlacement === 0
      ) {
        return fluid;
      }
      if (typeof block.Width !== 'undefined' && block.Width === 2) {
        return fluid;
      }

      // not fluid
      return '';
    },
    hasBackgroundColor (block) {
      return block.__type__ === 'ProductsInActionBlock' || (typeof block.ColorOptions !== 'undefined' && block.ColorOptions.BackgroundColor !== null && TinyColor(block.ColorOptions.BackgroundColor).isValid());
    },

    blockStyle (block) {
      if (block.DarkMode) {
        return {
          font: '#fcfcfc',
          background: '#000000'
        };
      }
      if (block.__type__ === 'KeyFeaturesBlock' && (!block.ColorOptions.FontColor && !block.ColorOptions.BackgroundColor)) {
        return {
          color: '#fcfcfc',
          background: '#000000'
        };
      }
      if (block.__type__ === 'ProductTechBlock') {
        return {
          background: '#f5f5f5'
        };
      }
      if (typeof block.ColorOptions !== 'undefined') {
        const colors = {
          font: TinyColor(block.ColorOptions.FontColor),
          background: TinyColor(block.ColorOptions.BackgroundColor),
          gradient: TinyColor(block.ColorOptions.GradientTo)
        };
        const bColor = colors.background.isValid() ? colors.background.toHexString() : '';
        let fColor;
        if (colors.font.isValid()) {
          fColor = colors.font.toHexString();
        } else if (bColor !== '') {
          fColor = TinyColor.mostReadable(bColor, ['#FCFCFC', '#1D1D1D']).toHexString();
        }
        if (colors.gradient.isValid()) {
          fColor = TinyColor.mostReadable(colors.gradient, ['#FCFCFC', '#1D1D1D']).toHexString();
          return {
            backgroundImage: `linear-gradient(${colors.background.toHexString()}, ${colors.gradient.toHexString()})`,
            color: fColor
          };
        }
        return {
          backgroundColor: bColor,
          color: fColor,
          '--linkColor': fColor
        };
      }
      return {};
    },
    scrollto () {
      hashCheckCount++;
      this.$nextTick(() => {
        const url = window.location.href;
        const idx = url.indexOf('#');
        const hash = idx !== -1 ? url.substring(idx + 1) : '';
        const el = document.getElementById(hash);
        if (hash && !el && hashCheckCount < 10) {
          setTimeout(() => {
            this.scrollto();
          }, 500);
        } else if (hash && el) {
          let scrollOptions = { offset: 0 };
          const bnd = el.getBoundingClientRect();
          if (bnd && bnd.top <= 100) {
            scrollOptions = { offset: -120 };
          }
          this.scrollToBlockIndex = this.blockList ? this.blockList.findIndex(b => b.AnchorId === hash) : -1;

          ScrollTo.scrollTo(`#${hash}`, 0.33, scrollOptions);
        }
      });
    }
  },
  updated () {
    printedBlocks = [];
  },
  created () {
    const visiterGroupString = this.$cookies.get('visitorgroups');
    if (visiterGroupString) {
      const arr = visiterGroupString.split(',');
      if (arr.length > 0) {
        this.userVisitorGroups = arr;
      }
    }
  },
  mounted () {
  }
};
</script>
